/**
 * Profile.js
 * Author: Markus.van.Kempen@gmail.com
 * Date: 2nd Nov 2025
 */

import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useToast } from './ToastContainer';
import ThemeSelector from './ThemeSelector';
import OrderHistory from './OrderHistory';
import MyDownloads from './MyDownloads';
import AddressForm from './AddressForm';
import { saveCustomerCountry } from '../utils/customerCountry';
import { isEmailVerified } from '../utils/emailVerified';
import './Profile.css';

const PROFILE_TAB_IDS = ['personal', 'orders', 'downloads', 'theme', 'account'];

const Profile = () => {
  const { currentUser, logout, updateCurrentUser } = useAuth();
  const { showSuccess, showError } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [isResendingVerification, setIsResendingVerification] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('personal');

  const [profile, setProfile] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    newsletter: false,
  });
  const [address, setAddress] = useState({
    street: '',
    street2: '',
    city: '',
    province: '',
    state: '',
    region: '',
    postalCode: '',
    country: 'Canada',
    countryName: '',
    countryCode: 'CA',
  });

  useEffect(() => {
    const fetchProfile = async () => {
      if (!currentUser) return;

      try {
        const token = localStorage.getItem('token');
        if (token) {
          const response = await fetch('/api/users/me', {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          });

          if (response.ok) {
            const apiProfile = await response.json();
            console.log('📋 Loaded profile from API:', apiProfile);

            const verified = isEmailVerified(apiProfile);
            updateCurrentUser({
              ...currentUser,
              ...apiProfile,
              email_verified: verified,
            });

            setProfile({
              firstName: apiProfile.first_name || '',
              lastName: apiProfile.last_name || '',
              email: apiProfile.email || currentUser.email || '',
              phone: apiProfile.phone || '',
              newsletter: apiProfile.newsletter_subscribed || false,
            });

            const loadedAddress = {
              street: apiProfile.address_line1 || '',
              street2: apiProfile.address_line2 || '',
              city: apiProfile.city || '',
              province: apiProfile.state_province || '',
              state: apiProfile.state_province || '',
              region: apiProfile.state_province || '',
              postalCode: apiProfile.postal_code || '',
              country: apiProfile.country || 'Canada',
              countryName: apiProfile.country || '',
              countryCode: apiProfile.country === 'United States' ? 'US' : apiProfile.country === 'Canada' ? 'CA' : 'INTL',
            };
            setAddress(loadedAddress);
            saveCustomerCountry(loadedAddress);

            return;
          } else {
            console.log('Failed to fetch from API, falling back to localStorage');
          }
        }
      } catch (error) {
        console.error('Error fetching profile from API:', error);
      }

      // Fallback to localStorage
      const savedProfile = localStorage.getItem(`userProfile_${currentUser.username}`);
      if (savedProfile) {
        const parsed = JSON.parse(savedProfile);
        setProfile({
          firstName: parsed.firstName || '',
          lastName: parsed.lastName || '',
          email: parsed.email || currentUser.email || '',
          phone: parsed.phone || '',
          newsletter: parsed.newsletter || false,
        });
        const loadedAddress = {
          street: parsed.addressLine1 || parsed.street || '',
          street2: parsed.addressLine2 || parsed.street2 || '',
          city: parsed.city || '',
          province: parsed.province || '',
          state: parsed.state || '',
          region: parsed.region || '',
          postalCode: parsed.postalCode || '',
          country: parsed.country || 'Canada',
          countryName: parsed.countryName || '',
          countryCode: parsed.countryCode || (parsed.country === 'United States' ? 'US' : parsed.country === 'Canada' ? 'CA' : 'INTL'),
        };
        setAddress(loadedAddress);
        saveCustomerCountry(loadedAddress);
      } else {
        setProfile({
          firstName: currentUser.firstName || '',
          lastName: currentUser.lastName || '',
          email: currentUser.email || '',
          phone: '',
          newsletter: false,
        });
        setAddress({
          street: '',
          street2: '',
          city: '',
          province: '',
          state: '',
          region: '',
          postalCode: '',
          country: 'Canada',
          countryName: '',
          countryCode: 'CA',
        });
      }
    };

    fetchProfile();
  }, [currentUser, updateCurrentUser]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setProfile(prevProfile => ({
      ...prevProfile,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isEmailVerified(currentUser)) {
      showError('Please verify your email to save changes.');
      return;
    }

    if (profile.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(profile.email)) {
      showError('Please enter a valid email address');
      return;
    }

    setIsSaving(true);

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        console.error('No token found');
        return;
      }

      let stateProvince = '';
      if (address.countryCode === 'CA') {
        stateProvince = address.province;
      } else if (address.countryCode === 'US') {
        stateProvince = address.state;
      } else {
        stateProvince = address.region;
      }

      const response = await fetch('/api/users/me', {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          first_name: profile.firstName,
          last_name: profile.lastName,
          phone: profile.phone,
          address_line1: address.street,
          address_line2: address.street2,
          city: address.city,
          state_province: stateProvince,
          postal_code: address.postalCode,
          country: address.country,
          newsletter_subscribed: profile.newsletter
        })
      });

      if (response.status === 401) {
        console.log('401 Unauthorized - token expired, logging out');
        logout();
        return;
      }

      if (response.ok) {
        const combinedData = {
          ...profile,
          ...address,
          addressLine1: address.street,
          addressLine2: address.street2,
          province: stateProvince
        };
        localStorage.setItem(`userProfile_${currentUser.username}`, JSON.stringify(combinedData));
        saveCustomerCountry(address);
        showSuccess('Profile updated successfully!');
      } else {
        const error = await response.json();
        showError(error.error || 'Failed to update profile. Please try again.');
      }
    } catch (error) {
      showError('Failed to update profile. Please try again.');
      console.error('Profile update error:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleResendVerification = async () => {
    setIsResendingVerification(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/users/resend-verification', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ email: currentUser.email })
      });

      const data = await response.json();

      if (response.ok && (data.success || data.alreadyVerified)) {
        if (data.alreadyVerified && token) {
          const meRes = await fetch('/api/users/me', {
            headers: { Authorization: `Bearer ${token}` },
          });
          if (meRes.ok) {
            const userData = await meRes.json();
            updateCurrentUser({
              ...currentUser,
              ...userData,
              email_verified: isEmailVerified(userData),
            });
          }
          showSuccess('Your email is already verified.');
        } else {
          showSuccess('Verification email sent! Please check your inbox.');
        }
      } else {
        showError(data.error || 'Failed to send verification email. Please try again.');
      }
    } catch (error) {
      console.error('Error resending verification email:', error);
      showError('Network error. Please try again.');
    } finally {
      setIsResendingVerification(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirmation.toLowerCase() !== 'delete') {
      showError('Please type "DELETE" to confirm account deletion');
      return;
    }

    setIsDeleting(true);

    try {
      const token = localStorage.getItem('token') || localStorage.getItem('authToken');

      if (!token) {
        showError('You must be logged in to delete your account.');
        setIsDeleting(false);
        setShowDeleteModal(false);
        setDeleteConfirmation('');
        window.location.href = '/register';
        return;
      }

      const response = await fetch('/api/users/me', {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const username = currentUser.username;
        localStorage.removeItem(`userProfile_${username}`);
        localStorage.removeItem('authToken');
        localStorage.removeItem('token');
        localStorage.removeItem('currentUser');
        sessionStorage.removeItem('currentUser');

        try {
          logout();
        } catch (error) {
          console.error('Logout error:', error);
        }

        showSuccess('Your account has been deleted successfully. Redirecting...');

        setTimeout(() => {
          window.location.href = '/';
        }, 1500);
      } else {
        const data = await response.json();
        let errorMessage = data.error || 'Failed to delete account';

        if (response.status === 401) {
          errorMessage = 'Your session has expired. Please log in again.';
          localStorage.removeItem('authToken');
          localStorage.removeItem('token');
          localStorage.removeItem('currentUser');
        } else if (response.status === 403 && data.details) {
          errorMessage = data.details;
        }

        showError(errorMessage);
      }
    } catch (error) {
      console.error('Delete account error:', error);
      showError('An error occurred while deleting your account.');
    } finally {
      setIsDeleting(false);
      setShowDeleteModal(false);
      setDeleteConfirmation('');
    }
  };

  const tabs = [
    { id: 'personal', label: 'Personal Info', icon: '👤' },
    { id: 'orders', label: 'Orders', icon: '📦' },
    { id: 'downloads', label: 'Downloads', icon: '📥' },
    { id: 'theme', label: 'Appearance', icon: '🎨' },
    { id: 'account', label: 'Account', icon: '⚙️' },
  ];

  useEffect(() => {
    const tab = searchParams.get('tab');
    if (tab && PROFILE_TAB_IDS.includes(tab)) {
      setActiveTab(tab);
    }
  }, [searchParams]);

  useEffect(() => {
    if (activeTab !== 'personal' || window.location.hash !== '#newsletter-settings') return;
    const t = window.setTimeout(() => {
      document.getElementById('newsletter-settings')?.scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      });
    }, 200);
    return () => clearTimeout(t);
  }, [activeTab, searchParams]);

  const renderTabContent = () => {
    switch (activeTab) {
      case 'personal':
        return (
          <div className="tab-content">
            <div className="tab-header">
              <h3>Personal Information</h3>
              <p>Manage your personal details and shipping address.</p>
            </div>
            <form onSubmit={handleSubmit} className="profile-form">
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="firstName">First Name</label>
                  <input
                    type="text"
                    id="firstName"
                    name="firstName"
                    value={profile.firstName}
                    onChange={handleChange}
                    placeholder="First name"
                    disabled={!emailVerified}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="lastName">Last Name</label>
                  <input
                    type="text"
                    id="lastName"
                    name="lastName"
                    value={profile.lastName}
                    onChange={handleChange}
                    placeholder="Last name"
                    disabled={!emailVerified}
                  />
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="email">Email Address</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={profile.email}
                  onChange={handleChange}
                  placeholder="Enter your email address"
                  autoComplete="email"
                  disabled={!emailVerified}
                />
                {currentUser && (
                  <div className={`email-verification-status ${emailVerified ? 'verified' : 'unverified'}`}>
                    {emailVerified ? (
                      <span className="verification-badge verified">✓ Email Verified</span>
                    ) : (
                      <div className="verification-badge-container">
                        <span className="verification-badge unverified">⚠️ Email Not Verified</span>
                        <button
                          type="button"
                          onClick={handleResendVerification}
                          disabled={isResendingVerification}
                          className="resend-verification-link"
                        >
                          {isResendingVerification ? 'Sending...' : 'Resend Verification Email'}
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="phone">Phone Number</label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={profile.phone}
                  onChange={handleChange}
                  placeholder="Enter your phone number"
                  autoComplete="tel"
                  disabled={!emailVerified}
                />
              </div>

              <div className="form-section-divider">
                <h4>Shipping Address</h4>
              </div>

              <AddressForm
                address={address}
                onChange={setAddress}
                disabled={isSaving || !emailVerified}
              />

              <div className="form-group-checkbox" id="newsletter-settings">
                <input
                  type="checkbox"
                  id="newsletter"
                  name="newsletter"
                  checked={profile.newsletter}
                  onChange={handleChange}
                  disabled={!emailVerified}
                />
                <label htmlFor="newsletter">Subscribe to our newsletter</label>
              </div>

              <button
                type="submit"
                className="save-button"
                disabled={!emailVerified || isSaving}
              >
                {isSaving ? 'Saving...' : 'Save Changes'}
              </button>
            </form>
          </div>
        );

      case 'orders':
        return (
          <div className="tab-content">
            <div className="tab-header">
              <h3>Order History</h3>
              <p>View your orders and cart inquiries emailed from checkout.</p>
            </div>
            <OrderHistory />
          </div>
        );

      case 'downloads':
        return (
          <div className="tab-content">
            <div className="tab-header">
              <h3>Digital Downloads</h3>
              <p>Albums you purchased as digital downloads. First download is immediate; repeat downloads need email confirmation.</p>
            </div>
            <MyDownloads />
          </div>
        );

      case 'theme':
        return (
          <div className="tab-content">
            <div className="tab-header">
              <h3>Appearance</h3>
              <p>Customize how the site looks for you.</p>
            </div>
            <ThemeSelector />
          </div>
        );

      case 'account':
        return (
          <div className="tab-content">
            <div className="tab-header">
              <h3>Account Settings</h3>
              <p>Manage your account preferences and security.</p>
            </div>

            <div className="account-info-card">
              <h4>Account Details</h4>
              <div className="account-details">
                <div className="detail-row">
                  <span className="detail-label">Username</span>
                  <span className="detail-value">{currentUser?.username || 'N/A'}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Email</span>
                  <span className="detail-value">{currentUser?.email || 'N/A'}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Email Status</span>
                  <span className={`detail-value status-badge ${emailVerified ? 'verified' : 'unverified'}`}>
                    {emailVerified ? 'Verified' : 'Not Verified'}
                  </span>
                </div>
              </div>
            </div>

            <div className="danger-zone">
              <h4>Danger Zone</h4>
              <p className="danger-text">
                Once you delete your account, there is no going back. All your data will be permanently removed.
              </p>
              <button
                type="button"
                onClick={() => setShowDeleteModal(true)}
                className="delete-account-button"
              >
                Delete My Account
              </button>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  const emailVerified = isEmailVerified(currentUser);

  return (
    <>
      {/* Profile Header */}
      <div className="profile-header">
        <div className="profile-avatar">
          {profile.firstName ? profile.firstName[0].toUpperCase() : currentUser?.username?.[0]?.toUpperCase() || '?'}
        </div>
        <div className="profile-info">
          <h1>{profile.firstName && profile.lastName ? `${profile.firstName} ${profile.lastName}` : currentUser?.username || 'User'}</h1>
          <p className="profile-email">{profile.email || currentUser?.email || ''}</p>
        </div>
      </div>

      {/* Tab Navigation - Separate Container */}
      <div className="profile-tabs-container">
        <div className="profile-tabs">
          {tabs.map(tab => (
            <button
              key={tab.id}
              className={`tab-button ${activeTab === tab.id ? 'active' : ''}`}
              onClick={() => {
                setActiveTab(tab.id);
                setSearchParams(tab.id === 'personal' ? {} : { tab: tab.id }, { replace: true });
              }}
            >
              <span className="tab-icon">{tab.icon}</span>
              <span className="tab-label">{tab.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Tab Content - Separate Container */}
      <div className="profile-content-container">
        <div className="profile-content">
          {renderTabContent()}
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="modal-overlay" onClick={() => !isDeleting && setShowDeleteModal(false)}>
          <div className="modal-content danger-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Delete Account</h3>
            <p>Are you absolutely sure? This action cannot be undone. This will permanently delete your account and remove all of your data.</p>

            <div className="delete-confirmation">
              <label htmlFor="deleteConfirmation">
                Type <strong>DELETE</strong> to confirm:
              </label>
              <input
                type="text"
                id="deleteConfirmation"
                value={deleteConfirmation}
                onChange={(e) => setDeleteConfirmation(e.target.value)}
                placeholder="Type DELETE"
                disabled={isDeleting}
              />
            </div>

            <div className="modal-actions">
              <button
                type="button"
                onClick={() => {
                  setShowDeleteModal(false);
                  setDeleteConfirmation('');
                }}
                disabled={isDeleting}
                className="cancel-button"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleDeleteAccount}
                disabled={isDeleting || deleteConfirmation.toLowerCase() !== 'delete'}
                className="confirm-delete-button"
              >
                {isDeleting ? 'Deleting...' : 'Delete Account'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Profile;
